import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { motion } from 'framer-motion';
import PageHero from '@/components/shared/PageHero';
import { publicApi } from '@/lib/api';
import { getIcon } from '@/lib/icons';
import useSEO from '@/hooks/useSEO';
import { ArrowRight, CheckCircle2, X } from 'lucide-react';

const StatCard = ({ label, value }) => (
    <Card className="bg-card border-border">
        <CardContent className="p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground line-clamp-2">
                {label}
            </div>
            <div className="mt-1.5 font-display font-semibold text-2xl md:text-3xl text-[hsl(var(--primary))] tabular-nums">
                {value}
            </div>
        </CardContent>
    </Card>
);

export default function SectorAchievementsPage() {
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const [lightbox, setLightbox] = useState(null);

    useSEO({
        title: 'Sector Achievements',
        description:
            'VVVSI Trust sector-wise achievements — Employment Services, Career Guidance, Education, Health, Green Visionary, Disaster Management and Other Social Welfare. Each sector shows activities, statistics, photo gallery and supporting content.',
    });

    useEffect(() => {
        publicApi
            .getSectorAchievements()
            .then((d) => {
                setItems(d);
                if (d.length && !active) setActive(d[0].sector_slug);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const activeItem = items.find((x) => x.sector_slug === active);

    return (
        <div data-testid="sector-achievements-page">
            <PageHero
                eyebrow="Sector Achievements"
                title="Impact, broken down by sector."
                subtitle="Seven programs. One integrated mission. Click a sector to see activities, statistics, photos and supporting details."
            />

            <section className="section-pad">
                <div className="container-wide grid lg:grid-cols-12 gap-8">
                    {/* Sector list menu (left) */}
                    <aside className="lg:col-span-4 xl:col-span-3">
                        <div className="lg:sticky lg:top-24">
                            <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-3">
                                Sectors
                            </div>
                            <div className="space-y-1" data-testid="sector-list">
                                {items.map((s) => {
                                    const Icon = getIcon(s.icon);
                                    const isActive = active === s.sector_slug;
                                    return (
                                        <button
                                            key={s.sector_slug}
                                            type="button"
                                            onClick={() => setActive(s.sector_slug)}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                                                isActive
                                                    ? 'bg-[hsl(var(--primary))] text-primary-foreground'
                                                    : 'bg-card border border-border hover:bg-[hsl(var(--secondary))]'
                                            }`}
                                            data-testid={`sector-link-${s.sector_slug}`}
                                        >
                                            <Icon
                                                className={`h-4 w-4 shrink-0 ${
                                                    isActive
                                                        ? 'text-[hsl(var(--accent))]'
                                                        : 'text-[hsl(var(--primary))]'
                                                }`}
                                            />
                                            <span
                                                className={`text-sm font-medium flex-1 ${
                                                    isActive
                                                        ? 'text-primary-foreground'
                                                        : 'text-foreground/85'
                                                }`}
                                            >
                                                {s.sector_name}
                                            </span>
                                            <ArrowRight
                                                className={`h-3.5 w-3.5 shrink-0 ${
                                                    isActive
                                                        ? 'opacity-100 text-[hsl(var(--accent))]'
                                                        : 'opacity-0'
                                                }`}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </aside>

                    {/* Active sector detail */}
                    <div className="lg:col-span-8 xl:col-span-9">
                        {activeItem && (
                            <motion.div
                                key={activeItem.sector_slug}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35 }}
                                data-testid="sector-detail"
                                className="space-y-8"
                            >
                                {/* Hero image + heading */}
                                <div>
                                    {activeItem.image_url && (
                                        <div className="aspect-[16/6] rounded-2xl overflow-hidden bg-muted mb-6">
                                            <img
                                                src={activeItem.image_url}
                                                alt={activeItem.sector_name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    )}
                                    <div className="flex items-center gap-3 mb-2">
                                        <Badge className="bg-[hsl(var(--accent))]/15 text-[hsl(30_90%_28%)] border-[hsl(var(--accent))]/30">
                                            Sector
                                        </Badge>
                                    </div>
                                    <h2
                                        className="font-display text-3xl sm:text-4xl font-semibold tracking-tight"
                                        data-testid="sector-heading"
                                    >
                                        {activeItem.sector_name}
                                    </h2>
                                    {activeItem.tagline && (
                                        <p className="mt-2 text-lg text-muted-foreground">
                                            {activeItem.tagline}
                                        </p>
                                    )}
                                    {activeItem.description && (
                                        <p
                                            className="mt-4 text-foreground/85 leading-relaxed"
                                            data-testid="sector-brief"
                                        >
                                            {activeItem.description}
                                        </p>
                                    )}
                                </div>

                                {/* Activities Completed */}
                                {(activeItem.activities || []).length > 0 && (
                                    <div data-testid="sector-activities">
                                        <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-3">
                                            Activities Completed
                                        </div>
                                        <ul className="grid sm:grid-cols-2 gap-2">
                                            {activeItem.activities.map((h) => (
                                                <li
                                                    key={h}
                                                    className="flex items-start gap-2 text-sm text-foreground/85"
                                                >
                                                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))] shrink-0" />
                                                    <span>{h}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Achievement Statistics */}
                                {(activeItem.stats || []).length > 0 && (
                                    <div data-testid="sector-stats">
                                        <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-3">
                                            Achievement Statistics
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {activeItem.stats.map((st, i) => (
                                                <StatCard
                                                    key={`${st.label}-${i}`}
                                                    label={st.label}
                                                    value={st.value}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Photo Gallery */}
                                {(activeItem.photo_gallery || []).length > 0 && (
                                    <div data-testid="sector-gallery">
                                        <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-3">
                                            Photo Gallery
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {activeItem.photo_gallery.map((url, idx) => (
                                                <button
                                                    key={`${url}-${idx}`}
                                                    type="button"
                                                    onClick={() => setLightbox(url)}
                                                    className="aspect-square bg-muted rounded-md overflow-hidden group"
                                                    data-testid={`sector-photo-${idx}`}
                                                >
                                                    <img
                                                        src={url}
                                                        alt=""
                                                        loading="lazy"
                                                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Supporting Content (Rich HTML) */}
                                {activeItem.supporting_content && (
                                    <div data-testid="sector-supporting-content">
                                        <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-3">
                                            Supporting Content
                                        </div>
                                        <div
                                            className="prose prose-sm max-w-none text-foreground/85 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-1 [&_strong]:text-foreground [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:font-display [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-2 [&_a]:text-[hsl(var(--primary))] [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-[hsl(var(--accent))] [&_blockquote]:pl-4 [&_blockquote]:italic"
                                            dangerouslySetInnerHTML={{
                                                __html: activeItem.supporting_content,
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Key partners (kept from legacy) */}
                                {(activeItem.key_partners || []).length > 0 && (
                                    <div>
                                        <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-3">
                                            Key Partners
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {activeItem.key_partners.map((k) => (
                                                <Badge
                                                    key={k}
                                                    variant="secondary"
                                                    className="bg-[hsl(var(--secondary))] text-[hsl(var(--primary))]"
                                                >
                                                    {k}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </div>
                </div>
            </section>

            {/* Photo Lightbox */}
            <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
                <DialogContent className="max-w-5xl p-0 bg-black/95 border-none">
                    <DialogHeader className="sr-only">
                        <DialogTitle>Photo</DialogTitle>
                    </DialogHeader>
                    <button
                        type="button"
                        onClick={() => setLightbox(null)}
                        className="absolute top-2 right-2 h-9 w-9 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center z-10"
                        aria-label="Close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                    <div className="flex items-center justify-center min-h-[60vh]">
                        {lightbox && (
                            <img
                                src={lightbox}
                                alt=""
                                className="max-h-[85vh] max-w-full object-contain"
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
